import React from 'react';
import DOMPurify from 'dompurify';
import "./ResultCard.css";
import ScoreGauge from "./ScoreGauge";
import VerdictBanner from "./VerdictBanner";
import EvidenceGrid from "./EvidenceGrid";
import ImageVerificationStatus from "./ImageVerificationStatus";
import { API_BASE_URL } from "../config";

const ResultCard = ({ data }) => {
  if (!data) return null;

  // Color from verdict label first (v2 adds "Unverified"/"Disputed"), else by score.
  const getVerdictColor = (score, label) => {
    if (label === "Unverified") return "#8A8F98"; // neutral gray — insufficient evidence
    if (label === "Disputed") return "#C99A3B";   // amber — credible sources conflict
    if (label === "Developing") return "#4A73B0"; // blue — corroborated but unconfirmed
    if (score >= 75) return "#4E8D5B"; // Sage green
    if (score >= 50) return "#C99A3B"; // Warm amber
    return "#B5533A"; // Warm clay red
  };

  const truthScore = data.truth_score || 0;
  const verdictLabel = data.verdict?.verdict_label;
  const scoreColor = getVerdictColor(truthScore, verdictLabel);
  // For non-conclusive results the numeric % is not a "truth" reading — relabel it.
  const scoreCaption = verdictLabel === "Unverified" ? "Confidence: Low"
    : verdictLabel === "Disputed" ? "Sources Conflict"
    : verdictLabel === "Developing" ? "Developing Story"
    : "Truth Score";

  return (
    <div className="result-container">
      {/* SCORE SECTION */}
      <div className="score-container">
        <ScoreGauge score={truthScore} color={scoreColor} />
        <div className="score-label">{scoreCaption}</div>
      </div>

      {/* VERDICT BANNER */}
      <VerdictBanner verdict={data.verdict} scoreColor={scoreColor} />

      {/* SOURCE IMAGE/PREVIEW SECTION */}
      {(data.thumbnail_url || (data.input_type === 'image' && data.media_id)) && (
        <div className="card source-preview-card">
          <h3>Source Content</h3>
          <div className="source-image-container">
            <img
              src={data.thumbnail_url || `${API_BASE_URL}/media/${data.media_id}`}
              alt="Source context"
              className="source-preview-image"
            />
          </div>
        </div>
      )}

      {/* TRUST STATEMENT */}
      {data.verdict?.trust_statement && (
        <div className="trust-statement-callout">
          {data.verdict.trust_statement}
        </div>
      )}

      {/* IMAGE VERIFICATION SECTION */}
      {data.input_type !== 'text' && data.image_verification && (
        <ImageVerificationStatus imageData={data.image_verification} />
      )}

      {/* SUMMARY SECTION */}
      <div className="card">
        <h3>Summary</h3>
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.summary || "No summary available") }} />
      </div>

      {/* DETAILED ANALYSIS SECTION */}
      {data.analysis && (
        <div className="card ai-analysis-card">
          <h3>AI Analysis</h3>
          <p className="analysis-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.analysis) }} />
        </div>
      )}

      {/* BIAS ANALYSIS SECTION */}
      <div className="card">
        <h3>Bias Analysis</h3>
        <div className="bias-analysis-content">
          <div className="bias-spectrum">
            <div className={`bias-point ${data.bias === 'Left' ? 'active-left' : ''}`}>
              <span className="bias-icon">🔵</span>
              <span className="bias-label">Left</span>
            </div>
            <div className={`bias-point ${data.bias === 'Lean Left' ? 'active-lean-left' : ''}`}>
              <span className="bias-icon">🔵</span>
              <span className="bias-label">Lean Left</span>
            </div>
            <div className={`bias-point ${data.bias === 'Center' ? 'active-center' : ''}`}>
              <span className="bias-icon">⚪</span>
              <span className="bias-label">Center</span>
            </div>
            <div className={`bias-point ${data.bias === 'Lean Right' ? 'active-lean-right' : ''}`}>
              <span className="bias-icon">🔴</span>
              <span className="bias-label">Lean Right</span>
            </div>
            <div className={`bias-point ${data.bias === 'Right' ? 'active-right' : ''}`}>
              <span className="bias-icon">🔴</span>
              <span className="bias-label">Right</span>
            </div>
          </div>

          {data.bias_data?.indicators && data.bias_data.indicators.length > 0 && (
            <div className="bias-indicators-container">
              {data.bias_data.indicators.map((indicator, index) => (
                <span key={index} className="bias-indicator-tag">
                  {indicator}
                </span>
              ))}
            </div>
          )}

          <p className="bias-description">
            {data.bias_data?.subjectivity > 0.5
              ? "This article contains a significant amount of subjective language and emotional framing."
              : "This article is primarily factual in tone, with minor partisan leanings detected."}
          </p>
        </div>
      </div>

      {/* EVIDENCE SECTION */}
      <EvidenceGrid data={data} />

      {/* KEYWORDS SECTION */}
      <div className="card">
        <h3>Key Topics</h3>
        {data.keywords && data.keywords.length > 0 ? (
          <ul className="keywords">
            {data.keywords.map((k, i) => (
              <li key={i}>{k}</li>
            ))}
          </ul>
        ) : (
          <p style={{ color: "var(--text-muted)" }}>No keywords found</p>
        )}
      </div>
    </div>
  );
};

export default ResultCard;